// src/lib/apiClient.js
import axios from 'axios';
import supabase from './supabaseClient';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
});

// CSRF token cache
let csrfToken = null;
let csrfTokenExpiry = null;

/**
 * Fetch CSRF token from the server
 * Caches the token until it expires
 */
async function getCsrfToken(forceRefresh = false) {
  // Return cached token if still valid (with 5 min buffer)
  if (!forceRefresh && csrfToken && csrfTokenExpiry && Date.now() < csrfTokenExpiry) {
    return csrfToken;
  }
  if (forceRefresh) { csrfToken = null; csrfTokenExpiry = null; }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const authToken = session?.access_token || localStorage.getItem('auth_token');

    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    // CSRF endpoint is at /api/csrf-token from server root
    const csrfUrl = API_BASE.includes('/api')
      ? `${API_BASE}/csrf-token`
      : `${API_BASE}/api/csrf-token`;

    const response = await fetch(csrfUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch CSRF token');
    }

    const data = await response.json();
    csrfToken = data.csrfToken;
    // Set expiry to 5 minutes before actual expiry for safety
    csrfTokenExpiry = Date.now() + ((data.expiresIn - 300) * 1000);

    return csrfToken;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    return null; // Return null instead of throwing to not break requests
  }
}

apiClient.interceptors.request.use(
  async (config) => {
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }

    // Add CSRF token for state-changing methods
    const method = (config.method || 'GET').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrf = await getCsrfToken();
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Retry once with a fresh CSRF token on stale-token 403s
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;
    // Review sandbox: the server refuses every change from a reviewer. Tell Layout so it can show a
    // friendly notice instead of the page's generic failure message.
    if (response?.status === 403 && response.data?.code === 'REVIEWER_READ_ONLY' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('reviewer-blocked'));
    }
    if (
      response?.status === 403 &&
      !config._csrfRetried &&
      (response.data?.error === 'Invalid CSRF token' || response.data?.error === 'CSRF token missing')
    ) {
      config._csrfRetried = true;
      const csrf = await getCsrfToken(true);
      if (csrf) config.headers['X-CSRF-Token'] = csrf;
      return apiClient(config);
    }
    return Promise.reject(error);
  }
);

// A/B Testing API calls
export const experimentsAPI = {
  getAll: () => apiClient.get('/experiments'),
  create: (data) => apiClient.post('/experiments', data),
  getById: (id) => apiClient.get(`/experiments/${id}`),
  updateStatus: (id, status) => apiClient.put(`/experiments/${id}/status`, { status }),
  declareWinner: (id, winner) => apiClient.post(`/experiments/${id}/declare-winner`, { winner })
};

export default apiClient;